import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  TextField,
  CircularProgress,
  Tooltip,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  IconButton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Divider
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SyncIcon from "@mui/icons-material/Sync";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { fetchRedcapDashboard, fetchRedcapSyncHistory, triggerRedcapSync, updateRedcapInstrumentAbbreviation, fetchRedcapAnswerInstrumentSummary, queryRedcapAnswerSubjects } from "utils/api";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../constants/statusColors";
import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";
import PageHeader from "components/PageHeader";
import WorkspacePage from "components/WorkspacePage";
import { formatDateTime } from "../../utils/formatDateTime";
import SearchBar from "../../components/SearchBar";
import StyledDataGrid from "../../components/StyledDataGrid";

const REDCAP_STATUS_META = {
  COMPLETE: {
    label: "Complete",
    color: "#2e7d32",
  },
  UNVERIFIED: {
    label: "Unverified",
    color: "#ef6c00",
  },
  INCOMPLETE: {
    label: "Incomplete",
    color: "#c62828",
  },
  NO_DATA: {
    label: "No Data",
    color: "#9e9e9e",
  },
  NOT_APPLICABLE: {
    label: "Not Applicable",
    color: "#c7c7c7",
  },
  UNKNOWN: {
    label: "Unknown",
    color: "#455a64",
  },
};

const getRedcapStatusMeta = (status) => {
  const normalized = status || "UNKNOWN";
  return REDCAP_STATUS_META[normalized] || REDCAP_STATUS_META.UNKNOWN;
};

const REDCAP_STATUS_LEGEND_KEYS = [
  "COMPLETE",
  "UNVERIFIED",
  "INCOMPLETE",
  "NO_DATA",
  // Restore NOT_APPLICABLE here if REDCap starts returning it regularly.
  // "NOT_APPLICABLE",
  "UNKNOWN",
];

const RedcapStatusIconShape = ({ status, size = 22 }) => {
  const normalized = status || "UNKNOWN";
  const meta = getRedcapStatusMeta(normalized);

  if (["COMPLETE", "UNVERIFIED", "INCOMPLETE"].includes(normalized)) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill={meta.color} />
      </svg>
    );
  }

  if (normalized === "NO_DATA") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="#ffffff" stroke={meta.color} strokeWidth="2" />
      </svg>
    );
  }

  if (normalized === "NOT_APPLICABLE") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="#ffffff" stroke={meta.color} strokeWidth="2" />
        <line x1="7" y1="17" x2="17" y2="7" stroke={meta.color} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  const questionFontSize = Math.max(12, Math.round(size * 0.72));
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        color: meta.color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: `${questionFontSize}px`,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      ?
    </Box>
  );
};

const StatusDot = ({ status }) => {
  const displayStatus = status || "UNKNOWN";
  const meta = getRedcapStatusMeta(displayStatus);

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#fff", textAlign: "center" }}>
            {displayStatus}
          </Typography>
        </Box>
      }
    >
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={meta.label}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          p: 0,
          cursor: "help",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&:focus-visible": {
            outline: "2px solid #500000",
            outlineOffset: 2,
          },
        }}
      >
        <RedcapStatusIconShape status={displayStatus} />
      </Box>
    </Tooltip>
  );
};

const RedcapStatusLegend = ({ compact = false }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: compact ? "flex-start" : "flex-end",
      alignItems: "center",
      gap: compact ? 1.1 : 1.5,
      maxWidth: compact ? "none" : 640,
      minWidth: 0,
    }}
  >
    {REDCAP_STATUS_LEGEND_KEYS.map((status) => {
      const meta = REDCAP_STATUS_META[status];
      return (
        <Box key={status} sx={{ display: "inline-flex", alignItems: "center", gap: compact ? 0.45 : 0.75 }}>
          <RedcapStatusIconShape status={status} size={compact ? 18 : 22} />
          <Typography sx={{ fontSize: compact ? "0.78rem" : "0.85rem", color: "#424242", whiteSpace: "nowrap", lineHeight: 1.2 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{meta.label}</Box>
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const RedcapDashboardFooter = () => (
  <GridFooterContainer
    sx={{
      minHeight: 52,
      px: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
    }}
  >
    <RedcapStatusLegend compact />
    <GridPagination />
  </GridFooterContainer>
);

const getInstrumentHeaderText = (column) => {
  if (column?.abbreviation && column.abbreviation.trim()) {
    return column.abbreviation.trim();
  }
  if (column?.label && column.label.trim()) {
    return column.label.trim();
  }
  return column?.name || "";
};

const INSTRUMENT_HEADER_MIN_WIDTH = 60;
const INSTRUMENT_HEADER_MAX_WIDTH = 120;
const INSTRUMENT_HEADER_FONT_SIZE = "0.8rem";

const instrumentHeaderWidth = (text) => {
  const safeText = text || "";
  const estimatedWidth = Math.ceil(safeText.length * 8) + 20;
  return Math.min(INSTRUMENT_HEADER_MAX_WIDTH, Math.max(INSTRUMENT_HEADER_MIN_WIDTH, estimatedWidth));
};

const redcapDashboardGridSx = {
  flex: 1,
  "& .MuiDataGrid-columnHeader": {
    px: 0.5,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    px: 0,
  },
  "& .redcap-instrument-header": {
    width: "100%",
    minWidth: 0,
    display: "block",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const REDCAP_SYNC_MODE_OPTIONS = [
  { value: "FULL", label: "Full", helper: "Catalog, dashboard status, and raw answer index." },
  { value: "STATUS_ONLY", label: "Status Only", helper: "Catalog and completeness dashboard projection." },
  { value: "RAW_INDEX_ONLY", label: "Raw/Index Only", helper: "Catalog, raw records, and answer index." },
  { value: "CATALOG_ONLY", label: "Catalog Only", helper: "Project, instruments, and metadata only." },
];

const selectedSyncModeHelper = (mode) => (
  REDCAP_SYNC_MODE_OPTIONS.find((option) => option.value === mode)?.helper || ""
);

const renderCenteredHeader = (label, fontSize = "0.875rem") => (
  <Typography
    component="span"
    sx={{
      width: "100%",
      textAlign: "center",
      fontSize,
      fontWeight: 700,
      lineHeight: 1.2,
    }}
  >
    {label}
  </Typography>
);

const REDCAP_PROJECT_KEY = "default|1730";
const ANSWER_ANALYTICS_EXCLUDED_INSTRUMENTS = new Set([
  "medications",
  "ipgxinteraction",
]);

const isAnswerAnalyticsInstrument = (column) => {
  const name = String(column?.name || "").trim().toLowerCase();
  return Boolean(name) && !ANSWER_ANALYTICS_EXCLUDED_INSTRUMENTS.has(name);
};

const ANALYTICS_DISPLAY_FILTERS = [
  { value: "ALL", label: "All Chart Types" },
  { value: "DONUT", label: "Donut" },
  { value: "BAR", label: "Bar" },
];

const ANSWER_CHART_COLORS = ["#500000", "#1976d2", "#2e7d32", "#ed6c02", "#6a1b9a", "#00838f", "#c62828", "#455a64"];

const numberText = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : "0";
};

const percentOf = (part, total) => {
  const numerator = Number(part || 0);
  const denominator = Number(total || 0);
  if (!denominator) return 0;
  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
};

const fieldTypeLabel = (fieldType) => {
  if (!fieldType) return "unknown";
  const typeStr = String(fieldType).toLowerCase();
  if (typeStr === "yesno") return "YES/NO";
  return typeStr;
};

const fieldMatchesDisplayFilter = (field, filter) => {
  if (filter === "ALL") return true;
  if (filter === "UNSUPPORTED") return String(field.displayKind || "").startsWith("UNSUPPORTED");
  if (filter === "DONUT") return field.displayKind === "DONUT" || field.displayKind === "PROGRESS";
  return field.displayKind === filter;
};

const decodeHtmlEntities = (value) => {
  if (!value) return "";
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }
  return String(value)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;|&apos;/gi, "'");
};

const stripHtmlForDisplay = (value) => {
  const decoded = decodeHtmlEntities(String(value || ""));
  return decodeHtmlEntities(
    decoded
      .replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, " ")
      .replace(/<\/?\s*[a-z][a-z0-9:-]*\b[^>]*>/gi, " ")
  );
};

const cleanFieldLabel = (label, fallback) => {
  const source = label || fallback || "";
  const withoutHtml = stripHtmlForDisplay(source);
  const withoutParentheses = withoutHtml
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/\s*（[^）]*）/g, "");
  const cleaned = withoutParentheses.replace(/\s+/g, " ").trim();
  return cleaned || source;
};

const optionCodeLabel = (option) => {
  const code = option?.code ?? "";
  return cleanFieldLabel(option?.label || code || "Unknown", code || "Unknown");
};

const getProjectionSeverity = (state) => {
  if (!state || state === "FRESH") return "info";
  if (state === "STALE") return "warning";
  return "error";
};

const AnswerOptionRow = ({ option, total, color, onClick, showBar = true }) => {
  const percentage = percentOf(option.subjectCount, total);
  return (
    <Button
      fullWidth
      variant="text"
      onClick={onClick}
      sx={{
        justifyContent: "stretch",
        textAlign: "left",
        color: "text.primary",
        textTransform: "none",
        borderRadius: 0,
        px: 1,
        py: 0.75,
        "&:hover": {
          backgroundColor: "rgba(80, 0, 0, 0.06)",
        },
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "14px minmax(0, 1fr) auto", columnGap: 1, alignItems: "start", mb: showBar ? 0.5 : 0 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: color, mt: "0.34rem" }} />
          <Typography sx={{ minWidth: 0, fontSize: "0.84rem", fontWeight: 600, whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>
            {optionCodeLabel(option)}
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", whiteSpace: "nowrap", mt: "0.1rem" }}>
            {numberText(option.subjectCount)}
          </Typography>
        </Box>
        {showBar && (
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              bgcolor: "#eeeeee",
              "& .MuiLinearProgress-bar": {
                bgcolor: color,
              },
            }}
          />
        )}
      </Box>
    </Button>
  );
};

const MiniDonut = ({ options, total }) => {
  const safeTotal = Number(total || 0);
  let cursor = 0;
  const stops = (options || []).map((option, index) => {
    const start = cursor;
    const value = safeTotal ? (Number(option.subjectCount || 0) / safeTotal) * 100 : 0;
    cursor += value;
    const color = ANSWER_CHART_COLORS[index % ANSWER_CHART_COLORS.length];
    return `${color} ${start}% ${cursor}%`;
  });
  const gradient = safeTotal && stops.length ? `conic-gradient(${stops.join(", ")})` : "#eeeeee";

  return (
    <Box
      sx={{
        width: 116,
        height: 116,
        borderRadius: "50%",
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 70,
          height: 70,
          borderRadius: "50%",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #eeeeee",
        }}
      >
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#500000", lineHeight: 1 }}>
          {numberText(total)}
        </Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", textTransform: "uppercase", lineHeight: 1.2 }}>
          subjects
        </Typography>
      </Box>
    </Box>
  );
};

const AnswerFieldCard = ({ field, onOpenDetails }) => {
  const options = field.options || [];
  const chartTotal = Math.max(Number(field.answeredSubjects || 0), options.reduce((sum, option) => sum + Number(option.subjectCount || 0), 0));
  const isUnsupported = !field.queryable || String(field.displayKind || "").startsWith("UNSUPPORTED");
  const title = cleanFieldLabel(field.fieldLabel, field.fieldName);

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "6px",
        p: 2,
        bgcolor: "#fff",
        minHeight: 260,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: "#212121", fontSize: "0.95rem", lineHeight: 1.25, whiteSpace: "normal", overflowWrap: "anywhere" }}>
            {title}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={fieldTypeLabel(field.fieldType)}
          sx={{ ...BASE_CHIP_SX, bgcolor: isUnsupported ? "#f5f5f5" : "#f8eeee", color: isUnsupported ? "#616161" : "#500000" }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>Total {numberText(field.totalSubjects)}</Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>Answered {numberText(field.answeredSubjects)}</Typography>
        <Button
          variant="text"
          size="small"
          disabled={!field.queryable || !field.missingSubjects}
          onClick={() => onOpenDetails(field, null, "EMPTY")}
          sx={{ minWidth: 0, p: 0, color: "#500000", fontSize: "0.78rem", textTransform: "none", lineHeight: 1.4 }}
        >
          Missing {numberText(field.missingSubjects)}
        </Button>
      </Box>

      {isUnsupported ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fafafa", border: "1px dashed #d6d6d6", p: 2 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "0.86rem", textAlign: "center" }}>
            {field.excludedReason || "This field is not available for answer analytics."}
          </Typography>
        </Box>
      ) : field.displayKind === "DONUT" || field.displayKind === "PROGRESS" ? (
        <Box sx={{ flex: 1, display: "flex", gap: 2, alignItems: "center", minWidth: 0 }}>
          <MiniDonut options={options} total={chartTotal} />
          <Box sx={{ flex: 1, minWidth: 0, maxHeight: 146, overflow: "auto" }}>
            {options.map((option, index) => (
              <AnswerOptionRow
                key={option.code || index}
                option={option}
                total={chartTotal}
                color={ANSWER_CHART_COLORS[index % ANSWER_CHART_COLORS.length]}
                onClick={() => onOpenDetails(field, option)}
                showBar={false}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {options.map((option, index) => (
            <AnswerOptionRow
              key={option.code || index}
              option={option}
              total={chartTotal}
              color={ANSWER_CHART_COLORS[index % ANSWER_CHART_COLORS.length]}
              onClick={() => onOpenDetails(field, option)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const RedcapDashboard = () => {
  const [activeTab, setActiveTab] = useState("completeness");
  const [data, setData] = useState({ records: [], instrumentColumns: [], totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const [searchName, setSearchName] = useState("");
  const [searchStatus, setSearchStatus] = useState("ALL");
  
  const [sortBy, setSortBy] = useState("redcapFormId");
  const [sortDirection, setSortDirection] = useState("asc");

  // Sync features state
  const [syncHistoryModalOpen, setSyncHistoryModalOpen] = useState(false);
  const [syncHistoryData, setSyncHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [manualSyncModalOpen, setManualSyncModalOpen] = useState(false);
  const [manualSyncMode, setManualSyncMode] = useState("FULL");
  const [manualSyncFormId, setManualSyncFormId] = useState("");
  const [manualSyncForceRematch, setManualSyncForceRematch] = useState(false);
  const [manualSyncDryRun, setManualSyncDryRun] = useState(false);
  const [syncToast, setSyncToast] = useState({ open: false, message: "", severity: "info" });
  const [latestSyncInfo, setLatestSyncInfo] = useState(null);

  const [abbreviationModalOpen, setAbbreviationModalOpen] = useState(false);
  const [abbreviationDrafts, setAbbreviationDrafts] = useState({});
  const [abbreviationSaving, setAbbreviationSaving] = useState(false);
  const [abbreviationMessage, setAbbreviationMessage] = useState(null);

  const [analyticsInstrument, setAnalyticsInstrument] = useState("");
  const [analyticsSearch, setAnalyticsSearch] = useState("");
  const [analyticsDisplayKind, setAnalyticsDisplayKind] = useState("ALL");
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailQuery, setDetailQuery] = useState(null);
  const [detailData, setDetailData] = useState({ records: [], totalElements: 0 });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(20);

  // Parse Form IDs: "1,3-7,9" -> ["1","3","4","5","6","7","9"]
  const parseFormIds = (input) => {
    if (!input || !input.trim()) return [];
    const result = new Set();
    const parts = input.split(',');
    for (let part of parts) {
      part = part.trim();
      if (!part) continue;
      if (part.includes('-')) {
        const [startStr, endStr] = part.split('-');
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            result.add(i.toString());
          }
        } else {
          result.add(part);
        }
      } else {
        result.add(part);
      }
    }
    return Array.from(result);
  };

  const resetManualSyncForm = () => {
    setManualSyncMode("FULL");
    setManualSyncFormId("");
    setManualSyncForceRematch(false);
    setManualSyncDryRun(false);
  };

  const showSyncToast = (message, severity = "info") => {
    setSyncToast({ open: true, message, severity });
  };

  const loadAnalyticsSummary = async () => {
    if (!analyticsInstrument) {
      setAnalyticsSummary(null);
      return;
    }

    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const summary = await fetchRedcapAnswerInstrumentSummary({
        redcapProjectKey: REDCAP_PROJECT_KEY,
        instrumentName: analyticsInstrument,
      });
      setAnalyticsSummary(summary || null);
    } catch (err) {
      console.error(err);
      setAnalyticsSummary(null);
      setAnalyticsError("Failed to load REDCap answer analytics. The summary API may not be available yet.");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadDetailData = async () => {
    if (!detailQuery) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await queryRedcapAnswerSubjects({
        ...detailQuery.request,
        page: detailPage,
        size: detailRowsPerPage,
      });
      setDetailData(response || { records: [], totalElements: 0 });
    } catch (err) {
      console.error(err);
      setDetailData({ records: [], totalElements: 0 });
      setDetailError("Failed to load matching subjects.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenAnswerDetails = (field, option, operator = "EQ") => {
    const effectiveOperator = operator || "EQ";
    const optionCode = option?.code != null ? String(option.code) : "";
    const displayFieldLabel = cleanFieldLabel(field.fieldLabel, field.fieldName);
    setDetailQuery({
      title: effectiveOperator === "EMPTY"
        ? `${displayFieldLabel}: Missing`
        : `${displayFieldLabel}: ${optionCodeLabel(option)}`,
      request: {
        redcapProjectKey: analyticsSummary?.redcapProjectKey || REDCAP_PROJECT_KEY,
        formName: field.formName || analyticsSummary?.instrumentName || analyticsInstrument,
        fieldName: field.fieldName,
        operator: effectiveOperator,
        values: effectiveOperator === "EMPTY" ? [] : [optionCode],
        deduplicateBySubject: true,
        sortBy: "redcapFormId",
        sortDirection: "asc",
      },
    });
    setDetailPage(0);
    setDetailRowsPerPage(20);
    setDetailData({ records: [], totalElements: 0 });
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setDetailQuery(null);
    setDetailError(null);
    setDetailData({ records: [], totalElements: 0 });
  };

  const handleOpenSyncHistory = async () => {
    setSyncHistoryModalOpen(true);
    setHistoryLoading(true);
    try {
      const history = await fetchRedcapSyncHistory();
      setSyncHistoryData(history || []);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseManualSyncModal = () => {
    setManualSyncModalOpen(false);
    resetManualSyncForm();
  };

  const handleRunManualSync = () => {
    setManualSyncModalOpen(false);
    const mode = manualSyncMode;
    const catalogOnly = mode === "CATALOG_ONLY";
    const forceRematchAllowed = mode === "FULL" || mode === "STATUS_ONLY";
    const formIdsList = catalogOnly ? [] : parseFormIds(manualSyncFormId);
    const forceRematch = forceRematchAllowed ? manualSyncForceRematch : false;
    const dryRun = manualSyncDryRun;
    resetManualSyncForm();
    showSyncToast("REDCap sync request submitted.", "info");
    triggerRedcapSync({
      mode,
      formIds: formIdsList,
      forceRematch,
      dryRun,
    })
      .then(async (response) => {
        if (response) {
          setLatestSyncInfo((current) => ({
            ...current,
            ...response,
          }));
        }
        await loadData();
      })
      .catch((err) => {
        console.error(err);
        showSyncToast("Failed to submit REDCap sync request.", "error");
      });
  };

  const handleOpenAbbreviationModal = () => {
    const drafts = {};
    (data.instrumentColumns || []).forEach((column) => {
      drafts[column.name] = column.abbreviation || "";
    });
    setAbbreviationDrafts(drafts);
    setAbbreviationMessage(null);
    setAbbreviationModalOpen(true);
  };

  const handleCloseAbbreviationModal = () => {
    if (!abbreviationSaving) {
      setAbbreviationModalOpen(false);
      setAbbreviationDrafts({});
      setAbbreviationMessage(null);
    }
  };

  const handleSaveAbbreviation = async () => {
    const changedColumns = (data.instrumentColumns || []).filter((column) => (
      (abbreviationDrafts[column.name] ?? "") !== (column.abbreviation || "")
    ));
    if (changedColumns.length === 0) {
      handleCloseAbbreviationModal();
      return;
    }
    setAbbreviationSaving(true);
    setAbbreviationMessage(null);
    try {
      const updatedInstruments = await Promise.all(changedColumns.map((column) => (
        updateRedcapInstrumentAbbreviation(column.name, abbreviationDrafts[column.name] ?? "")
      )));
      const updatedByName = updatedInstruments.reduce((acc, instrument, index) => {
        const name = instrument?.instrumentName || changedColumns[index].name;
        acc[name] = instrument?.instrumentAbbreviation ?? abbreviationDrafts[name] ?? "";
        return acc;
      }, {});
      setData((current) => ({
        ...current,
        instrumentColumns: (current.instrumentColumns || []).map((column) => (
          Object.prototype.hasOwnProperty.call(updatedByName, column.name)
            ? { ...column, abbreviation: updatedByName[column.name] }
            : column
        )),
      }));
      setAbbreviationMessage({ type: "success", text: "Abbreviations saved." });
      setTimeout(() => {
        handleCloseAbbreviationModal();
      }, 800);
    } catch (err) {
      console.error(err);
      setAbbreviationMessage({ type: "error", text: "Failed to save abbreviation." });
    } finally {
      setAbbreviationSaving(false);
    }
  };

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRedcapDashboard({
        page,
        size: rowsPerPage,
        name: searchName,
        status: searchStatus === "ALL" ? "" : searchStatus,
        sortBy,
        sortDirection
      });
      if (response && response.records) {
        setData(response);
      } else {
        setData({ records: [], instrumentColumns: [], totalElements: 0 });
      }

      try {
        const history = await fetchRedcapSyncHistory();
        if (history && history.length > 0) {
          const latestSuccess = history.find(r => r.status === 'SUCCESS' || r.status === 'COMPLETED');
          setLatestSyncInfo(latestSuccess || history[0]);
        }
      } catch (e) {
        console.error("Failed to load latest sync info", e);
      }

    } catch (err) {
      console.error(err);
      setError("Failed to load REDCap dashboard data.");
    } finally {
      setLoading(false);
    }
  }

  const answerAnalyticsInstrumentColumns = (data.instrumentColumns || []).filter(isAnswerAnalyticsInstrument);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchName, searchStatus, sortBy, sortDirection]);

  useEffect(() => {
    if (!data.instrumentColumns?.length) {
      return;
    }

    const selectedAvailable = answerAnalyticsInstrumentColumns.some((column) => column.name === analyticsInstrument);
    if (!analyticsInstrument || !selectedAvailable) {
      setAnalyticsInstrument(answerAnalyticsInstrumentColumns[0]?.name || "");
      setAnalyticsSummary(null);
      setAnalyticsError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsInstrument, data.instrumentColumns]);

  useEffect(() => {
    if (activeTab === "analytics" && analyticsInstrument) {
      loadAnalyticsSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsInstrument]);

  useEffect(() => {
    if (detailModalOpen && detailQuery) {
      loadDetailData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailModalOpen, detailQuery, detailPage, detailRowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchName("");
    setSearchStatus("ALL");
    setPage(0);
  };

  const getMatchStatusChip = (status) => {
    return (
      <Chip
        label={status || "UNKNOWN"}
        size="small"
        sx={{
          ...BASE_CHIP_SX,
          ...getStatusChipStyle(status || "UNKNOWN"),
        }}
      />
    );
  };

  const formatSyncMode = (row) => {
    const requested = row?.requestedMode;
    const effective = row?.effectiveMode || row?.exportMode;
    if (requested && effective && requested !== effective) {
      return `${requested} -> ${effective}`;
    }
    return effective || requested || row?.exportMode || "-";
  };

  const catalogOnlyMode = manualSyncMode === "CATALOG_ONLY";
  const forceRematchDisabled = manualSyncMode === "RAW_INDEX_ONLY" || manualSyncMode === "CATALOG_ONLY";

  const columns = [
    { field: 'redcapFormId', headerName: 'Form', width: 50, headerAlign: 'center', align: 'center', sortable: false, renderHeader: () => renderCenteredHeader('Form') },
    { field: 'displayName', headerName: 'Participant Name', flex: 1, minWidth: 220, headerAlign: 'center', align: 'center', sortable: false, renderHeader: () => renderCenteredHeader('Participant Name'),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2">{params.row.displayName}</Typography>
        </Box>
      )
    },
    { field: 'patientAccountNumber', headerName: 'MRN', width: 80, headerAlign: 'center', align: 'center', sortable: false, renderHeader: () => renderCenteredHeader('MRN', INSTRUMENT_HEADER_FONT_SIZE),
      renderCell: (params) => params.row.patientAccountNumber || params.row.patientId || '-'
    },
    { field: 'matchStatus', headerName: 'Biobank Match Status', width: 160, headerAlign: 'center', align: 'center', sortable: false, renderHeader: () => renderCenteredHeader('Biobank Match Status'),
      renderCell: (params) => getMatchStatusChip(params.value)
    },
    ...(data.instrumentColumns || []).map(col => {
      const headerText = getInstrumentHeaderText(col);
      const headerWidth = instrumentHeaderWidth(headerText);
      return {
        field: col.name,
        headerName: headerText,
        width: headerWidth,
        minWidth: INSTRUMENT_HEADER_MIN_WIDTH,
        maxWidth: INSTRUMENT_HEADER_MAX_WIDTH,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderHeader: () => (
          <Tooltip title={col.label || col.name} arrow>
            <Typography
              className="redcap-instrument-header"
              component="span"
              sx={{
                fontSize: INSTRUMENT_HEADER_FONT_SIZE,
                fontWeight: 700,
              }}
            >
              {headerText}
            </Typography>
          </Tooltip>
        ),
        renderCell: (params) => {
          const inst = params.row.instruments?.[col.name];
          return <StatusDot status={inst?.aggregateStatus} />;
        }
      };
    })
  ];

  const syncColumns = [
    { field: 'startedAt', headerName: 'Started At', flex: 1.5, minWidth: 160, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.startedAt ? formatDateTime(params.row.startedAt) : '-'
    },
    { field: 'duration', headerName: 'Duration', flex: 1, minWidth: 90, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => {
        if (!params.row.startedAt || !params.row.completedAt) return '-';
        const diffMs = new Date(params.row.completedAt) - new Date(params.row.startedAt);
        if (diffMs < 0) return '-';
        const s = Math.floor(diffMs / 1000);
        return `${Math.floor(s / 60)}m ${s % 60}s`;
      }
    },
    { field: 'status', headerName: 'Status', flex: 1, minWidth: 120, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => (
        <Chip 
          size="small" 
          label={params.value} 
          sx={{
            ...BASE_CHIP_SX,
            ...getStatusChipStyle(params.value),
          }}
        />
      )
    },
    { field: 'currentPhase', headerName: 'Phase', flex: 1, minWidth: 110, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.currentPhase || '-'
    },
    { field: 'runType', headerName: 'Type', flex: 0.8, minWidth: 90, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.runType || '-'
    },
    { field: 'exportMode', headerName: 'Mode', flex: 1.2, minWidth: 120, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => (
        <Tooltip title={`Requested: ${params.row.requestedMode || '-'} | Effective: ${params.row.effectiveMode || params.row.exportMode || '-'}`} arrow>
          <Typography sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
            {formatSyncMode(params.row)}
          </Typography>
        </Tooltip>
      )
    },
    { field: 'distinctFormIdCount', headerName: 'Form Count', flex: 0.8, minWidth: 110, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.distinctFormIdCount ?? '-'
    },
    { field: 'rawRowsUpserted', headerName: 'Rows Upserted', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.rawRowsUpserted ?? '-'
    },
    { field: 'subjectsMatched', headerName: 'Patients Matched', flex: 1, minWidth: 140, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.subjectsMatched ?? '-'
    },
    { field: 'instrumentStatusesUpserted', headerName: 'Statuses Upserted', flex: 1, minWidth: 155, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.instrumentStatusesUpserted ?? '-'
    },
    { field: 'answerIndexRowsUpserted', headerName: 'Answers Upserted', flex: 1, minWidth: 155, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.answerIndexRowsUpserted ?? '-'
    },
  ];

  const abbreviationColumns = [
    { 
      field: 'instrument', 
      headerName: 'Instrument', 
      flex: 1.2, 
      minWidth: 200,
      headerAlign: 'center', 
      align: 'center', 
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word', py: 1, lineHeight: 1.2 }}>
          {params.row.label || params.row.name}
        </Typography>
      )
    },
    { 
      field: 'currentHeader', 
      headerName: 'Current Header', 
      flex: 1, 
      minWidth: 150,
      headerAlign: 'center', 
      align: 'center', 
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.84rem', whiteSpace: 'normal', wordBreak: 'break-word', py: 1, lineHeight: 1.2 }}>
          {getInstrumentHeaderText(params.row)}
        </Typography>
      )
    },
    { 
      field: 'abbreviation', 
      headerName: 'Abbreviation', 
      flex: 0.8,
      minWidth: 120,
      headerAlign: 'center', 
      align: 'center', 
      sortable: false,
      renderCell: (params) => {
        const column = params.row;
        return (
          <Box sx={{ width: '100%', py: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={abbreviationDrafts[column.name] ?? ""}
              onChange={(event) => setAbbreviationDrafts((current) => ({
                ...current,
                [column.name]: event.target.value,
              }))}
              disabled={abbreviationSaving}
              inputProps={{ style: { textAlign: 'center' } }}
            />
          </Box>
        );
      }
    }
  ];

  const analyticsFields = (analyticsSummary?.fields || []).filter((field) => {
    const search = analyticsSearch.trim().toLowerCase();
    const matchesSearch = !search
      || String(field.fieldName || "").toLowerCase().includes(search)
      || String(field.fieldLabel || "").toLowerCase().includes(search);
    return matchesSearch && fieldMatchesDisplayFilter(field, analyticsDisplayKind);
  });

  const projectionState = analyticsSummary?.answerIndexProjection?.state;

  const detailColumns = [
    { field: 'subjectName', headerName: 'Name', flex: 1.2, minWidth: 180, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.value || '-'
    },
    { field: 'redcapFormId', headerName: 'Form', flex: 0.6, minWidth: 90, headerAlign: 'center', align: 'center', sortable: false },
    { field: 'matchStatus', headerName: 'Match Status', flex: 0.9, minWidth: 130, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => getMatchStatusChip(params.value)
    },
    { field: 'patientAccountNumber', headerName: 'MRN', flex: 0.8, minWidth: 110, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => params.row.patientAccountNumber || '-'
    },
  ];

  return (
    <WorkspacePage>
      <PageHeader
        title="REDCap Data"
        tabs={
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
          >
            <Tab value="completeness" label="Completeness" />
            <Tab value="analytics" label="Answer Analytics" />
          </Tabs>
        }
      />

      {activeTab === "completeness" ? (
        <>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchBar
            placeholder="Patient Name"
            value={searchName}
            onChange={(e) => { setSearchName(e.target.value); setPage(0); }}
            onClear={() => { setSearchName(""); setPage(0); }}
            width={140}
            sx={{ minWidth: 140 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={searchStatus}
              onChange={(e) => { setSearchStatus(e.target.value); setPage(0); }}
              sx={{ borderRadius: 0 }}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="MATCHED">MATCHED</MenuItem>
              <MenuItem value="UNMATCHED">UNMATCHED</MenuItem>
              <MenuItem value="AMBIGUOUS">AMBIGUOUS</MenuItem>
              <MenuItem value="CONFLICT">CONFLICT</MenuItem>
            </Select>
          </FormControl>

          {(searchName || searchStatus !== "ALL") && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{
                textTransform: "uppercase",
                borderRadius: 0,
                borderColor: "#500000",
                color: "#500000",
                "&:hover": {
                  borderColor: "#500000",
                  backgroundColor: "rgba(80, 0, 0, 0.06)",
                },
              }}
            >
              CLEAR
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenAbbreviationModal}
          >
            Edit Headers
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleOpenSyncHistory}
          >
            Sync History
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={() => { setManualSyncModalOpen(true); }}
            sx={{ backgroundColor: "#500000", "&:hover": { backgroundColor: "#400000" } }}
          >
            Manual Sync
          </Button>
        </Box>
      </Box>

        {error && (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid
            sx={redcapDashboardGridSx}
            rows={data.records || []}
            columns={columns}
            getRowId={(row) => row.redcapSubjectKey || row.redcapFormId || Math.random().toString()}
            pageSizeOptions={[10, 25, 50, 100]}
            pagination
            paginationMode="server"
            rowCount={data.totalElements || 0}
            paginationModel={{ page, pageSize: rowsPerPage }}
            onPaginationModelChange={(model) => {
              if (model.pageSize !== rowsPerPage) {
                setRowsPerPage(model.pageSize);
                setPage(0);
              } else {
                setPage(model.page);
              }
            }}
            disableSelectionOnClick
            disableColumnSorting
            disableColumnMenu
            loading={loading}
            slots={{ footer: RedcapDashboardFooter }}
          />
        </Box>
        </>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ width: 260, flexShrink: 0 }}>
                <Select
                  displayEmpty
                  value={analyticsInstrument}
                  onChange={(event) => setAnalyticsInstrument(event.target.value)}
                  renderValue={(selected) => (
                    <Typography
                      component="span"
                      sx={{
                        display: "block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selected
                        ? (answerAnalyticsInstrumentColumns.find((column) => column.name === selected)?.label || selected)
                        : "Select Instrument"}
                    </Typography>
                  )}
                  sx={{
                    borderRadius: 0,
                    width: 260,
                    "& .MuiSelect-select": {
                      minWidth: 0,
                      pr: 4,
                    },
                  }}
                >
                  {answerAnalyticsInstrumentColumns.map((column) => (
                    <MenuItem key={column.name} value={column.name}>
                      <Typography sx={{ overflowWrap: "anywhere", whiteSpace: "normal" }}>
                        {column.label || column.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <SearchBar
                placeholder="Field contains"
                value={analyticsSearch}
                onChange={(e) => setAnalyticsSearch(e.target.value)}
                onClear={() => setAnalyticsSearch("")}
                width={180}
                sx={{ minWidth: 160 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={analyticsDisplayKind}
                  onChange={(e) => setAnalyticsDisplayKind(e.target.value)}
                  sx={{ borderRadius: 0 }}
                >
                  {ANALYTICS_DISPLAY_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(analyticsSearch || analyticsDisplayKind !== "ALL") && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setAnalyticsSearch("");
                    setAnalyticsDisplayKind("ALL");
                  }}
                  sx={{
                    textTransform: "uppercase",
                    borderRadius: 0,
                    borderColor: "#500000",
                    color: "#500000",
                    "&:hover": {
                      borderColor: "#500000",
                      backgroundColor: "rgba(80, 0, 0, 0.06)",
                    },
                  }}
                >
                  CLEAR
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleOpenSyncHistory}
              >
                Sync History
              </Button>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={() => { setManualSyncModalOpen(true); }}
                sx={{ backgroundColor: "#500000", "&:hover": { backgroundColor: "#400000" } }}
              >
                Manual Sync
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
            {analyticsError && (
              <Alert severity="error" icon={false} sx={{ mb: 2 }}>
                {analyticsError}
              </Alert>
            )}

            {projectionState && projectionState !== "FRESH" && (
              <Alert severity={getProjectionSeverity(projectionState)} icon={false} sx={{ mb: 2 }}>
                Answer index projection is {projectionState}. Counts are shown from the current available projection.
              </Alert>
            )}

            {analyticsLoading && (
              <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { backgroundColor: "#500000" } }} />
            )}

            {!analyticsInstrument ? (
              <Box sx={{ p: 4, textAlign: "center", border: "1px dashed #d6d6d6" }}>
                <Typography color="text.secondary">No answer analytics instruments are available.</Typography>
              </Box>
            ) : !analyticsLoading && !analyticsError && analyticsFields.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center", border: "1px dashed #d6d6d6" }}>
                <Typography color="text.secondary">No answer analytics fields match the current filters.</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 2,
                  pb: 1,
                }}
              >
                {analyticsFields.map((field) => (
                  <AnswerFieldCard
                    key={field.fieldName}
                    field={field}
                    onOpenDetails={handleOpenAnswerDetails}
                  />
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Manual Sync Dialog */}
      <Dialog open={manualSyncModalOpen} onClose={handleCloseManualSyncModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Trigger Manual Sync
          <IconButton
            onClick={handleCloseManualSyncModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ width: 140, fontWeight: 'medium', flexShrink: 0 }}>Mode</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={manualSyncMode}
                  onChange={(e) => {
                    const nextMode = e.target.value;
                    setManualSyncMode(nextMode);
                    if (nextMode === "CATALOG_ONLY") {
                      setManualSyncFormId("");
                      setManualSyncForceRematch(false);
                    } else if (nextMode === "RAW_INDEX_ONLY") {
                      setManualSyncForceRematch(false);
                    }
                  }}
                  renderValue={(selected) => (
                    REDCAP_SYNC_MODE_OPTIONS.find((option) => option.value === selected)?.label || "Full"
                  )}
                >
                  {REDCAP_SYNC_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ ml: '140px', mt: 0.5 }}>
              {selectedSyncModeHelper(manualSyncMode)}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ width: 140, fontWeight: 'medium', flexShrink: 0 }}>Form IDs</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., 1, 3-7, 9"
                value={manualSyncFormId}
                onChange={(e) => setManualSyncFormId(e.target.value)}
                disabled={catalogOnlyMode}
              />
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ ml: '140px', mt: 0.5 }}>
              {catalogOnlyMode
                ? "Catalog-only sync always runs at project scope."
                : "Optional scope, for example 1, 3-7, 9. Leave blank for the full project."}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ width: 140, fontWeight: 'medium', flexShrink: 0 }}>Force Rematch</Typography>
              <Switch
                checked={manualSyncForceRematch}
                onChange={(e) => setManualSyncForceRematch(e.target.checked)}
                disabled={forceRematchDisabled}
                color="primary"
                sx={{ ml: -1.5 }}
              />
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ ml: '140px', mt: -0.5 }}>
              {forceRematchDisabled
                ? "Available only when the sync writes the status projection."
                : "Rematch all subjects, ignoring existing matches."}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ width: 140, fontWeight: 'medium', flexShrink: 0 }}>Dry Run</Typography>
              <Switch
                checked={manualSyncDryRun}
                onChange={(e) => setManualSyncDryRun(e.target.checked)}
                color="primary"
                sx={{ ml: -1.5 }}
              />
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ ml: '140px', mt: -0.5 }}>
              Preview the sync path without writing GraphChain data.
            </Typography>
          </Box>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Alert severity="warning" icon={false} sx={{ py: 0, px: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}>
              <strong>Note:</strong> May take several minutes to complete. Are you sure you want to proceed?
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualSyncModal}>Cancel</Button>
          <Button onClick={handleRunManualSync} variant="contained">Run Sync</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={syncToast.open}
        autoHideDuration={1000}
        onClose={() => setSyncToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSyncToast((current) => ({ ...current, open: false }))}
          severity={syncToast.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {syncToast.message}
        </Alert>
      </Snackbar>

      {/* Answer Detail Dialog */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '50vh', maxHeight: '78vh', borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2, px: 3, fontSize: '1.25rem', fontWeight: 700 }}>
          Matching Subjects
          <IconButton
            onClick={handleCloseDetailModal}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 3, pt: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, color: "#500000", mb: 0.5 }}>
              {detailQuery?.title || "Answer detail"}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
              {numberText(detailData.totalElements)} subjects matched
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {detailError && (
            <Alert severity="error" icon={false} sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          <Box sx={{ height: 410, width: '100%' }}>
            <StyledDataGrid
              rows={detailData.records || []}
              columns={detailColumns}
              getRowId={(row) => `${row.redcapSubjectKey || "subject"}-${row.redcapFormId || "form"}-${row.patientAccountNumber || row.subjectName || "patient"}`}
              pageSizeOptions={[10, 20, 50]}
              pagination
              paginationMode="server"
              rowCount={detailData.totalElements || 0}
              paginationModel={{ page: detailPage, pageSize: detailRowsPerPage }}
              onPaginationModelChange={(model) => {
                if (model.pageSize !== detailRowsPerPage) {
                  setDetailRowsPerPage(model.pageSize);
                  setDetailPage(0);
                } else {
                  setDetailPage(model.page);
                }
              }}
              disableSelectionOnClick
              disableColumnSorting
              disableColumnMenu
              loading={detailLoading}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-topContainer': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Sync History Dialog */}
      <Dialog 
        open={syncHistoryModalOpen} 
        onClose={() => setSyncHistoryModalOpen(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '50vh', maxHeight: '75vh', borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2, px: 3, fontSize: '1.25rem', fontWeight: 700 }}>
          Sync History
          <IconButton
            onClick={() => setSyncHistoryModalOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 3, pt: 1 }}>
          {historyLoading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <StyledDataGrid
                rows={syncHistoryData}
                columns={syncColumns}
                getRowId={(row) => row.syncRunId || Math.random().toString()}
                disableSelectionOnClick
                disableColumnSorting
                disableColumnMenu
                hideFooter
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiDataGrid-topContainer': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiDataGrid-scrollbarFiller': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Instrument Abbreviation Dialog */}
      <Dialog 
        open={abbreviationModalOpen} 
        onClose={handleCloseAbbreviationModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '50vh', maxHeight: '75vh', borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2, px: 3, fontSize: '1.25rem', fontWeight: 700 }}>
          Edit Column Headers
          <IconButton
            onClick={handleCloseAbbreviationModal}
            sx={{ position: 'absolute', right: 12, top: 12 }}
            disabled={abbreviationSaving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 3, pt: 1 }}>
          <Box sx={{ height: 420, width: '100%' }}>
            <StyledDataGrid
              rows={data.instrumentColumns || []}
              columns={abbreviationColumns}
              getRowId={(row) => row.name || Math.random().toString()}
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 50}
              disableSelectionOnClick
              disableColumnSorting
              disableColumnMenu
              hideFooter
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-topContainer': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-scrollbarFiller': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            />
          </Box>
          {abbreviationMessage && (
            <Alert severity={abbreviationMessage.type} icon={false} sx={{ m: 2, py: 0, px: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}>
              {abbreviationMessage.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAbbreviationModal} disabled={abbreviationSaving}>Cancel</Button>
          <Button onClick={handleSaveAbbreviation} variant="contained" disabled={abbreviationSaving}>
            {abbreviationSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </WorkspacePage>
  );
};

export default RedcapDashboard;
